import {
  ExerciseCanvas,
  ExerciseVideoCard,
  roundRect,
  drawLimb,
  drawPersonHead,
  drawSneaker,
  drawCardFrame,
  drawChecklistPanel,
  drawPhasePill,
} from "@/components/we360/ExerciseCanvas";
import { useUserProfile } from "@/contexts/UserProfileContext";

function useIsFemale(sexProp) {
  let ctxSex = null;
  try {
    const ctx = useUserProfile();
    ctxSex = ctx?.profile?.sexo || ctx?.profile?.genero;
  } catch {
    // Allow rendering outside UserProfileProvider in isolated unit tests
  }
  const resolved = sexProp || ctxSex || "";
  return /^femenino|^mujer|^female|^f$/i.test(String(resolved).trim());
}

export function ParQSVG({ hasAlert }) {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f2-parq-canvas"
      renderFrame={(ctx, t) => {
        const pulse = Math.sin(t * Math.PI * 2);
        const accent = hasAlert ? "#C0453A" : "#00628F";
        const accentLight = hasAlert ? "#FCEAE8" : "#E3F0F5";

        drawCardFrame(ctx, {
          title: "Cuestionario PAR-Q (Seguridad)",
          subtitle: hasAlert
            ? "Se detectó al menos una respuesta afirmativa de precaución"
            : "Verifica que sea seguro realizar las pruebas físicas hoy",
          badgeText: hasAlert ? "Precaución" : "Chequeo",
          progress: t,
          isHighlight: !hasAlert && t > 0.5,
          accentColor: accent,
          accentLight,
        });

        // Clinical clipboard on left stage
        const clipX = 76;
        const clipY = 104;
        ctx.save();
        ctx.shadowColor = "rgba(45,42,36,0.08)";
        ctx.shadowBlur = 14;
        roundRect(ctx, clipX, clipY, 128, 176, 16);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.restore();

        roundRect(ctx, clipX, clipY, 128, 176, 16);
        ctx.strokeStyle = hasAlert ? "#C0453A" : "#00628F";
        ctx.lineWidth = 2;
        ctx.stroke();

        // Top clip
        roundRect(ctx, clipX + 38, clipY - 10, 52, 20, 8);
        ctx.fillStyle = hasAlert ? "#C0453A" : "#00628F";
        ctx.fill();

        // Animated checklist rows inside clipboard
        for (let i = 0; i < 4; i++) {
          const rowY = clipY + 34 + i * 32;
          const rowActive = t * 4 >= i;
          roundRect(ctx, clipX + 16, rowY, 20, 20, 6);
          ctx.fillStyle = rowActive
            ? hasAlert && i === 1
              ? "#FCEAE8"
              : "#E2F0E8"
            : "#F5EFE2";
          ctx.fill();

          ctx.strokeStyle = rowActive
            ? hasAlert && i === 1
              ? "#C0453A"
              : "#157346"
            : "#D5CBB8";
          ctx.lineWidth = 1.5;
          ctx.stroke();

          if (rowActive) {
            ctx.strokeStyle = hasAlert && i === 1 ? "#C0453A" : "#157346";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(clipX + 21, rowY + 10);
            ctx.lineTo(clipX + 25, rowY + 14);
            ctx.lineTo(clipX + 32, rowY + 6);
            ctx.stroke();
          }

          roundRect(ctx, clipX + 44, rowY + 6, 64 - (i % 2) * 12, 8, 4);
          ctx.fillStyle = rowActive ? "#00628F" : "#D5CBB8";
          ctx.fill();
        }

        // Standing athletic figure next to clipboard
        const groundY = 332;
        ctx.strokeStyle = "#D5CBB8";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(50, groundY);
        ctx.lineTo(355, groundY);
        ctx.stroke();

        const pX = 278;
        const ankleY = groundY - 8;
        const kneeY = ankleY - 56;
        const hipY = kneeY - 56;
        const shoulderY = hipY - 66 + pulse * 1.5;
        const headY = shoulderY - 24;

        // Back leg & front leg
        drawLimb(ctx, pX - 10, hipY, pX - 12, kneeY, 12, 9.5, "#1B2430");
        drawLimb(ctx, pX - 12, kneeY, pX - 12, ankleY, 9, 6.5, "#D9AF8B");
        drawSneaker(ctx, pX - 12, ankleY, "#004B6E");

        drawLimb(ctx, pX + 8, hipY, pX + 10, kneeY, 12.5, 9.5, "#263238");
        drawLimb(ctx, pX + 10, kneeY, pX + 10, ankleY, 9, 6.5, "#F0C49E");
        drawSneaker(ctx, pX + 10, ankleY, "#00628F");

        // Torso
        drawLimb(ctx, pX, hipY, pX, shoulderY, 15, 17, "#00628F");
        drawPersonHead(ctx, pX, shoulderY, pX, headY, -1);

        // Arm pointing/holding clipboard
        drawLimb(ctx, pX - 10, shoulderY + 6, pX - 42, shoulderY + 26, 8.5, 7, "#0077AC");
        drawLimb(ctx, pX - 42, shoulderY + 26, clipX + 124, clipY + 95, 6.5, 5.5, "#F0C49E");

        // Right arm on hip
        drawLimb(ctx, pX + 12, shoulderY + 6, pX + 32, shoulderY + 34, 8.5, 7, "#0077AC");
        drawLimb(ctx, pX + 32, shoulderY + 34, pX + 16, hipY - 4, 6.5, 5.5, "#F0C49E");

        // Pulsing heart badge on chest
        const hScale = 1 + Math.max(0, pulse) * 0.22;
        ctx.save();
        ctx.translate(pX, shoulderY + 26);
        ctx.scale(hScale, hScale);
        ctx.fillStyle = hasAlert ? "#C0453A" : "#157346";
        ctx.beginPath();
        ctx.arc(0, 0, 10, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.font = '800 10px "Plus Jakarta Sans", sans-serif';
        ctx.fillText(hasAlert ? "!" : "♥", -3.5, 3.5);
        ctx.restore();

        drawChecklistPanel(ctx, {
          title: "Seguridad Pre-Test",
          tips: [
            { label: "Salud cardiovascular", sub: "Sin dolor ni mareos", active: !hasAlert, warn: hasAlert },
            { label: "Control articular", sub: "Sin lesión aguda hoy", active: true },
            { label: "7 preguntas PAR-Q", sub: "Responde Sí o No", active: true },
          ],
        });

        drawPhasePill(ctx, {
          label: hasAlert
            ? "Atención: revisa la recomendación antes de continuar"
            : "Paso 1: Completa las 7 preguntas de aptitud física",
          isHighlight: !hasAlert,
          accentColor: accent,
          accentLight,
        });
      }}
    />
  );
}

export function WalkSVG({ meters, sex }) {
  const isFemale = useIsFemale(sex);
  const hasValue = Boolean(meters && parseFloat(meters) > 0);
  return (
    <ExerciseVideoCard
      testId="f2-walk-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_caminata_f.mp4"
          : "/videos/exercises/we360_ai_caminata.mp4"
      }
      title="Caminata de 6 Minutos (6MWT)"
      subtitle="Camina a paso rápido y constante en una superficie plana"
      badgeText="6:00 min"
      checklistTitle="Técnica de Caminata"
      tips={[
        { label: "Paso rápido constante", sub: "Sin correr ni trotar", active: true },
        { label: "Terreno plano", sub: "Pasillo o pista segura", active: true },
        {
          label: "Distancia total",
          sub: hasValue ? `Registrado: ${meters} m` : "Mide en metros al final",
          active: hasValue,
        },
      ]}
      phaseLabel="Mantén braceo activo y ritmo constante durante los 6 minutos"
      isHighlight={hasValue}
      rightBadgeText={hasValue ? `✓ ${meters} metros` : null}
    />
  );
}

export function SquatSVG({ reps, sex }) {
  const isFemale = useIsFemale(sex);
  const hasValue = Boolean(reps && parseInt(reps, 10) > 0);
  return (
    <ExerciseVideoCard
      testId="f2-squat-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_sentadilla_f.mp4"
          : "/videos/exercises/we360_ai_sentadilla.mp4"
      }
      title="Sentadillas en 30 segundos"
      subtitle="Baja con la espalda recta hasta que los muslos queden paralelos (90°)"
      badgeText="30 seg"
      checklistTitle="Técnica Correcta"
      tips={[
        { label: "Espalda neutra", sub: "Sin encorvar tronco", active: true },
        { label: "Muslos a 90°", sub: "Paralelos al piso abajo", active: true },
        { label: "Talones fijos", sub: "Pies al ancho de hombros", active: true },
      ]}
      phaseLabel="1. De pie → 2. Muslos paralelos (90°) → 3. Impulso controlado"
      isHighlight={hasValue}
      rightBadgeText={hasValue ? `✓ ${reps} reps` : null}
    />
  );
}

export function PushupSVG({ reps, sex }) {
  const isFemale = useIsFemale(sex);
  const hasValue = Boolean(reps && parseInt(reps, 10) > 0);
  return (
    <ExerciseVideoCard
      testId="f2-pushup-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_flexiones_f.mp4"
          : "/videos/exercises/we360_ai_flexiones.mp4"
      }
      title={
        isFemale
          ? "Flexiones de Pecho Modificadas (con rodillas)"
          : "Flexiones de Pecho (Push-ups)"
      }
      subtitle={
        isFemale
          ? "Apoya las rodillas sobre un mat o cojín y mantén la espalda recta al bajar"
          : "Mantén el cuerpo alineado en plancha y baja el pecho cerca del suelo"
      }
      badgeText="Sin pausa"
      checklistTitle="Guía de Ejecución"
      tips={[
        {
          label: isFemale ? "Tronco alineado" : "Plancha firme",
          sub: isFemale ? "Línea recta hombros-rodillas" : "Abdomen y glúteos activos",
          active: true,
        },
        { label: "Codos a 90° abajo", sub: "Pecho cerca del piso", active: true },
        {
          label: isFemale ? "Apoyo de rodillas" : "Opción modificada",
          sub: isFemale ? "Sobre mat o superficie suave" : "Válido si lo requieres",
          active: true,
        },
      ]}
      phaseLabel={
        isFemale
          ? "1. Rodillas apoyadas → 2. Pecho abajo (codos a 90°) → 3. Empuje recto"
          : "1. Plancha alta → 2. Pecho abajo (codos a 90°) → 3. Empuje recto"
      }
      isHighlight={hasValue}
      rightBadgeText={hasValue ? `✓ ${reps} reps` : null}
    />
  );
}

export function FlexibilitySVG({ flexibilidad, sex }) {
  const isFemale = useIsFemale(sex);
  const hasSelection = Boolean(flexibilidad);
  return (
    <ExerciseVideoCard
      testId="f2-flexibility-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_flexibilidad_f.mp4"
          : "/videos/exercises/we360_ai_flexibilidad.mp4"
      }
      title="Flexibilidad (Sit & Reach)"
      subtitle="Sentado/a con piernas extendidas, estira los brazos hacia tus pies"
      badgeText="Movilidad"
      checklistTitle="Guía Sit & Reach"
      tips={[
        { label: "Rodillas rectas", sub: "Sin flexionar piernas", active: true },
        { label: "Alcance suave", sub: "Exhala al estirar al frente", active: true },
        { label: "Sin rebotes", sub: "Mantén 2 seg el alcance", active: true },
      ]}
      phaseLabel="Siéntate erguido/a, desliza las manos hacia los pies y registra tu alcance"
      isHighlight={hasSelection}
    />
  );
}

export function F2SummarySVG({ complete }) {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f2-summary-canvas"
      renderFrame={(ctx, t) => {
        const pulse = (1 - Math.cos(t * Math.PI * 2)) / 2;

        drawCardFrame(ctx, {
          title: "Resumen de Condición Física (F2)",
          subtitle: complete
            ? "¡Evaluación lista! Revisa tus indicadores antes de confirmar"
            : "Confirma la casilla inferior para guardar tu evaluación F2",
          badgeText: complete ? "Completo" : "Revisión",
          progress: complete ? 1 : t,
          isHighlight: complete,
        });

        // Medal / Trophy emblem on left stage
        const cx = 205;
        const cy = 215;

        ctx.beginPath();
        ctx.arc(cx, cy, 68 + pulse * 6, 0, Math.PI * 2);
        ctx.fillStyle = complete ? "rgba(21,115,70,0.10)" : "rgba(0,98,143,0.08)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 52, 0, Math.PI * 2);
        ctx.fillStyle = complete ? "#157346" : "#00628F";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 42, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();

        // Checkmark inside emblem
        ctx.strokeStyle = complete ? "#157346" : "#00628F";
        ctx.lineWidth = 5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        ctx.moveTo(cx - 16, cy + 2);
        ctx.lineTo(cx - 4, cy + 14);
        ctx.lineTo(cx + 18, cy - 12);
        ctx.stroke();

        drawChecklistPanel(ctx, {
          title: "Componentes Evaluados",
          tips: [
            { label: "Capacidad Aeróbica", sub: "Test Caminata 6 min", active: true },
            { label: "Fuerza Muscular", sub: "Sentadillas + Flexiones", active: true },
            { label: "Movilidad Articular", sub: "Flexibilidad Sit & Reach", active: Boolean(complete) },
          ],
        });

        drawPhasePill(ctx, {
          label: complete
            ? "✓ Evaluación F2 verificada y lista para sincronizar"
            : "Marca la casilla de confirmación para finalizar",
          isHighlight: Boolean(complete),
        });
      }}
    />
  );
}
